import { Injectable } from '@angular/core';
import { RenewableSystemType } from '@brokerportal/common/enums';
import { HESLP_ALLOWED_SYSTEM_TYPES, HESLP_FLAGGED_SYSTEM_TYPES } from '@brokerportal/common/utils';
import { from, Observable } from 'rxjs';
import { concatMap, filter, map, take, toArray } from 'rxjs/operators';
import { FeatureFlagService } from './feature-flag.service';

/**
 * Resolves which HESLP (NSW Home Energy Saver Loan) equipment types a broker may currently select,
 * combining the always-available Solar/Battery base list with the per-type runtime feature flags.
 * Shared by the HESLP referral modal and the Confirm System Details modal so both surfaces agree.
 */
@Injectable({ providedIn: 'root' })
export class HeslpSystemTypesService {
    constructor(private featureFlagService: FeatureFlagService) {}

    /**
     * Flag-gated HESLP types whose flag is ON, in `HESLP_FLAGGED_SYSTEM_TYPES` order. Emits once and completes.
     * Flags are resolved sequentially so the first lookup warms the feature flag cache for the rest.
     */
    getEnabledFlaggedSystemTypes(): Observable<RenewableSystemType[]> {
        return from(HESLP_FLAGGED_SYSTEM_TYPES).pipe(
            concatMap(flagged =>
                this.featureFlagService.isEnabled(flagged.featureCode).pipe(
                    take(1),
                    map(enabled => (enabled ? flagged.type : null))
                )
            ),
            filter((type): type is RenewableSystemType => type !== null),
            toArray()
        );
    }

    /** Base HESLP allow-list followed by the currently enabled flag-gated types. Emits once and completes. */
    getAllowedSystemTypes(): Observable<RenewableSystemType[]> {
        return this.getEnabledFlaggedSystemTypes().pipe(
            map(enabledTypes => [...HESLP_ALLOWED_SYSTEM_TYPES, ...enabledTypes])
        );
    }
}
